import { ChevronRight, List, LocateFixed, Map as MapIcon, Route as RouteIcon, Search, Zap } from 'lucide-react'
import { useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router'
import { useActive, useStations } from '@/api/driver'
import type { MapStation } from '@/api/types'
import { EmptyState, ErrorState, LoadingBlock, StatusPill } from '@/components/common'
import { StationsMap } from '@/components/map'
import { useAuth } from '@/lib/auth'
import { connectorRate, connectorState } from '@/lib/charging'
import { count, kw, npr } from '@/lib/format'
import { distanceKm, openNow } from '@/lib/geo'
import { useI18n } from '@/lib/i18n'
import { cn } from '@/lib/utils'
import { useFavourites } from '@/lib/favourites'

type Filter = 'fav' | 'free' | 'dc' | 'CCS2' | 'GB/T' | 'Type 2' | 'CHAdeMO'
const FILTERS: { id: Filter; label: string }[] = [
  { id: 'fav', label: '♥ Favourites' },
  { id: 'free', label: 'Free now' },
  { id: 'dc', label: 'Fast (DC)' },
  { id: 'CCS2', label: 'CCS2' },
  { id: 'GB/T', label: 'GB/T' },
  { id: 'Type 2', label: 'Type 2' },
  { id: 'CHAdeMO', label: 'CHAdeMO' },
]

/** Free connectors on chargers that are online. */
export function freeConnectors(s: MapStation) {
  return s.chargers.flatMap((c) => c.connectors.filter((k) => connectorState(c, k).startable)).length
}
const maxPower = (s: MapStation) => Math.max(0, ...s.chargers.map((c) => c.max_power_kw ?? 0))
const lowestRate = (s: MapStation) => {
  const r = s.chargers.flatMap((c) => c.connectors.map(connectorRate)).filter((x): x is number => x != null)
  return r.length ? Math.min(...r) : null
}
const types = (s: MapStation) => [...new Set(s.chargers.flatMap((c) => c.connectors.map((k) => k.connector_type)))]

export function MapPage() {
  const { t } = useI18n()
  const navigate = useNavigate()
  const { user } = useAuth()
  const q = useStations()
  const active = useActive(!!user)
  const [search, setSearch] = useState('')
  const [filters, setFilters] = useState<Set<Filter>>(new Set())
  const [selected, setSelected] = useState<number | null>(null)
  const [view, setView] = useState<'map' | 'list'>('map')
  const [me, setMe] = useState<{ lat: number; lng: number } | null>(null)
  const [locating, setLocating] = useState(false)
  const [focus, setFocus] = useState<[number, number] | null>(null)
  const fav = useFavourites()
  const favKey = [...fav.ids].sort().join(',')

  const stations = useMemo(() => {
    const term = search.trim().toLowerCase()
    const list = (q.data?.stations ?? []).filter((s) => {
      if (term && !`${s.name} ${s.address ?? ''} ${s.cpo_name ?? ''}`.toLowerCase().includes(term)) return false
      if (filters.has('free') && freeConnectors(s) === 0) return false
      if (filters.has('fav') && !fav.ids.has(s.id)) return false
      if (filters.has('dc') && !s.chargers.some((c) => c.charger_type === 'DC')) return false
      for (const f of ['CCS2', 'GB/T', 'Type 2', 'CHAdeMO'] as const) if (filters.has(f) && !types(s).some((x) => x.toLowerCase().replace(/\s/g, '') === f.toLowerCase().replace(/\s/g, ''))) return false
      return true
    })
    const withDist = list.map((s) => ({ s, d: me && s.latitude != null && s.longitude != null ? distanceKm(me, { lat: s.latitude, lng: s.longitude }) : null }))
    return withDist.sort((a, b) => (a.d != null && b.d != null ? a.d - b.d : freeConnectors(b.s) - freeConnectors(a.s)))
  }, [q.data, search, filters, me, favKey]) // eslint-disable-line react-hooks/exhaustive-deps

  const pins = stations
    .filter(({ s }) => s.latitude != null && s.longitude != null)
    .map(({ s }) => ({ id: s.id, lat: s.latitude!, lng: s.longitude!, label: String(freeConnectors(s)), off: freeConnectors(s) === 0 }))
  const sel = stations.find(({ s }) => s.id === selected)

  const locate = () => {
    if (!navigator.geolocation) return
    setLocating(true)
    navigator.geolocation.getCurrentPosition(
      (p) => {
        const pos = { lat: p.coords.latitude, lng: p.coords.longitude }
        setMe(pos)
        setFocus([pos.lat, pos.lng])
        setLocating(false)
      },
      () => setLocating(false),
      { enableHighAccuracy: false, timeout: 10_000 },
    )
  }
  const toggle = (f: Filter) => setFilters((s) => { const n = new Set(s); if (n.has(f)) n.delete(f); else n.add(f); return n })
  const pick = (id: number) => {
    setSelected(id)
    const st = stations.find(({ s }) => s.id === id)?.s
    if (st?.latitude != null && st.longitude != null) setFocus([st.latitude, st.longitude])
  }
  const charging = active.data?.active_sessions[0]

  const list = (
    <div className="divide-y divide-border">
      {stations.map(({ s, d }) => (
        <StationRow key={s.id} station={s} distance={d} selected={s.id === selected} onClick={() => navigate(`/stations/${s.id}`)} onHover={() => setSelected(s.id)} />
      ))}
    </div>
  )

  return (
    <div className="relative flex min-h-0 flex-1 flex-col md:flex-row" style={{ height: 'calc(100dvh - 3.5rem)' }}>
      {/* Search and filters */}
      <div className="absolute inset-x-0 top-0 z-[500] space-y-2 p-3 md:static md:w-96 md:shrink-0 md:border-r md:border-border md:bg-surface md:p-4">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('Search stations or places')}
            aria-label={t('Search stations or places')}
            className="h-11 w-full rounded-xl border border-border bg-surface pl-9 pr-3 text-sm shadow-card focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/25 md:h-10 md:shadow-none"
          />
        </div>
        <div className="no-scrollbar -mx-3 flex gap-2 overflow-x-auto px-3 md:mx-0 md:flex-wrap md:px-0">
          {FILTERS.map((f) => (
            <button key={f.id} onClick={() => toggle(f.id)} className={cn('shrink-0 rounded-full border px-3 py-1 text-[13px] font-medium shadow-card md:shadow-none', filters.has(f.id) ? 'border-ink bg-ink text-white dark:border-brand dark:bg-brand dark:text-ink' : 'border-border bg-surface text-text')}>
              {t(f.label)}
            </button>
          ))}
        </div>
        {/* Desktop list */}
        <div className="-mx-4 hidden min-h-0 md:block" style={{ height: 'calc(100dvh - 3.5rem - 8.5rem)', overflowY: 'auto' }}>
          {q.isLoading ? <div className="p-4"><LoadingBlock rows={5} /></div> : q.error ? <ErrorState error={q.error} onRetry={() => q.refetch()} /> : stations.length ? list : <EmptyState title={t('No stations match')} />}
        </div>
      </div>

      {/* Map, or the list on phones */}
      <div className="relative min-h-0 flex-1">
        <StationsMap className={cn('absolute inset-0', view === 'list' && 'invisible md:visible')} pins={pins} selectedId={selected} onSelect={pick} me={me} focus={focus} />
        {view === 'list' && (
          <div className="absolute inset-0 overflow-y-auto bg-surface pb-36 pt-28 md:hidden">
            {q.isLoading ? <div className="p-4"><LoadingBlock rows={5} /></div> : q.error ? <ErrorState error={q.error} onRetry={() => q.refetch()} /> : stations.length ? list : <EmptyState title={t('No stations match')} />}
          </div>
        )}

        <div className="pointer-events-none absolute inset-x-0 bottom-20 z-[500] flex flex-col gap-3 px-3 md:bottom-4 md:items-end">
          {charging && (
            <Link to="/charging" className="pointer-events-auto flex items-center gap-3 rounded-2xl bg-ink p-3 text-white shadow-card md:w-96">
              <span className="grid size-9 place-items-center rounded-xl bg-brand text-ink"><Zap className="size-5 fill-current" /></span>
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-semibold">{t('Charging at {station}', { station: charging.station_name ?? charging.charger_id })}</span>
                <span className="block text-xs text-white/70">{`${charging.energy_kwh.toFixed(2)} kWh · ${npr(charging.cost)}`}</span>
              </span>
              <ChevronRight className="size-5 text-white/60" />
            </Link>
          )}
          {sel && view === 'map' && (
            <div className="pointer-events-auto md:hidden">
              <div className="overflow-hidden rounded-2xl border border-border bg-surface shadow-card">
                <StationRow station={sel.s} distance={sel.d} onClick={() => navigate(`/stations/${sel.s.id}`)} />
              </div>
            </div>
          )}
          <div className="pointer-events-auto flex justify-between gap-2 md:justify-end">
            <Link to="/route" className="flex h-11 items-center gap-2 rounded-full bg-ink px-4 text-sm font-medium text-white shadow-card">
              <RouteIcon className="size-4" /> {t('Plan a trip')}
            </Link>
            <button onClick={() => setView(view === 'map' ? 'list' : 'map')} className="flex h-11 items-center gap-2 rounded-full bg-surface px-4 text-sm font-medium shadow-card md:hidden">
              {view === 'map' ? <List className="size-4" /> : <MapIcon className="size-4" />} {view === 'map' ? t('List') : t('Map')}
            </button>
            <button onClick={locate} disabled={locating} aria-label={t('Near me')} className={cn('flex h-11 items-center gap-2 rounded-full bg-surface px-4 text-sm font-medium shadow-card', locating && 'opacity-60')}>
              <LocateFixed className={cn('size-4', me && 'text-info')} /> {t('Near me')}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

function StationRow({ station: s, distance, selected, onClick, onHover }: { station: MapStation; distance: number | null; selected?: boolean; onClick: () => void; onHover?: () => void }) {
  const { t } = useI18n()
  const free = freeConnectors(s)
  const open = openNow(s.opening_hours)
  const rate = lowestRate(s)
  return (
    <button onClick={onClick} onMouseEnter={onHover} className={cn('flex w-full items-start gap-3 px-4 py-3 text-left hover:bg-surface-2', selected && 'bg-surface-2')}>
      <span className={cn('mt-0.5 grid size-10 shrink-0 place-items-center rounded-xl text-sm font-bold', free ? 'bg-success-soft text-success' : 'bg-surface-3 text-text-2')}>{count(free)}</span>
      <span className="min-w-0 flex-1">
        <span className="block truncate font-medium">{s.name}</span>
        <span className="block truncate text-[13px] text-text-2">{[distance != null ? `${distance < 10 ? distance.toFixed(1) : Math.round(distance)} km` : null, s.address].filter(Boolean).join(' · ')}</span>
        <span className="mt-1.5 flex flex-wrap items-center gap-1.5">
          {free ? <StatusPill tone="success">{t('{n} free', { n: count(free) })}</StatusPill> : <StatusPill tone="neutral">{t('None free')}</StatusPill>}
          {open === false && <StatusPill tone="danger">{t('Closed now')}</StatusPill>}
          <span className="text-xs text-text-2">{[types(s).join(', '), maxPower(s) ? t('up to {kw}', { kw: kw(maxPower(s)) }) : null, rate != null ? `${npr(rate)}/kWh` : null].filter(Boolean).join(' · ')}</span>
        </span>
      </span>
      <ChevronRight className="mt-3 size-4 shrink-0 text-muted" />
    </button>
  )
}
